import {
  useEffect, useMemo,
} from 'react';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import {
  getSingleZIndex, ZIndexedEntity,
} from '~/map/zIndexes/zIndexRanges';
import { convertPlacesSearchToBoundingBox } from '~/places/places.helpers';
import { useMapSettingsPlacesSearchesSelector } from '~/store/mapSettings/places/mapSettingsPlaces.selectors';
import type { PlacesSearchId } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { useMapContext } from '../mapContext';

export const PlacesSearchRectangleOverlayContainer = ({
  searchId,
}: {
  searchId: PlacesSearchId;
}) => {
  const { webglLayers } = useMapContext();
  const placesSearches = useMapSettingsPlacesSearchesSelector();
  const zIndex = getSingleZIndex(ZIndexedEntity.PlacesAreaHighlight);

  const placesSearch = searchId ? placesSearches.find((search) => search.id === searchId) : null;

  const boundingBox = useMemo(() => {
    if (!placesSearch) {
      return null;
    }

    return convertPlacesSearchToBoundingBox(placesSearch);
  }, [placesSearch]);

  const points = useMemo(() => boundingBox ? ([
    boundingBox.getNorthEast(),
    {
      lat: boundingBox.getSouthWest().lat,
      lng: boundingBox.getNorthEast().lng,
    },
    boundingBox.getSouthWest(),
    {
      lat: boundingBox.getNorthEast().lat,
      lng: boundingBox.getSouthWest().lng,
    },
    boundingBox.getNorthEast(),
    // extra point to render a closed corner
    {
      lat: boundingBox.getSouthWest().lat,
      lng: boundingBox.getNorthEast().lng,
    },
  ]) : null, [boundingBox]);

  useEffect(() => {
    if (!placesSearch || !points) {
      return;
    }

    const polyline = new WebGLOverlay.Polyline({
      zIndex,
      width: 3,
      color: convertColorToWebGLColor(placesSearch.color),
      stroke: false,
    }, points);

    webglLayers.MapObjectPolylines.add(polyline);

    return () => {
      webglLayers.MapObjectPolylines.remove(polyline);
    };
  }, [placesSearch, points, webglLayers.MapObjectPolylines, zIndex]);

  return null;
};
